import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Drawer, EmptyState, MultiSelectChips } from '@saas-kit/ui';

import type { QmsAssignableStaff } from './apiCatalog';
import type { QmsCounter } from './types';

/** Pick which staff users may claim a counter. An empty selection means no
 *  claim-only staff can take it (managers always can). */
export function CounterAssignmentsDrawer({
  counter,
  staff,
  saving,
  onClose,
  onSave,
}: {
  counter: QmsCounter | null;
  staff: QmsAssignableStaff[];
  saving: boolean;
  onClose: () => void;
  onSave: (counterId: string, userIds: string[]) => void;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const [userIds, setUserIds] = useState<string[]>([]);

  useEffect(() => {
    setUserIds(counter?.assigned_user_ids ?? []);
  }, [counter]);

  return (
    <Drawer
      open={counter !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t('fields.assigned_staff')}
      footer={
        counter ? (
          <Button disabled={saving} onClick={() => onSave(counter.id, userIds)}>
            {t('actions.save')}
          </Button>
        ) : null
      }
    >
      <p className="mb-3 text-sm text-muted-foreground">
        {t('assignments.hint')}
      </p>
      {staff.length === 0 ? (
        <EmptyState title={t('assignments.no_staff')} />
      ) : (
        <MultiSelectChips
          options={staff.map((s) => ({ value: s.user_id, label: s.label }))}
          value={userIds}
          onChange={setUserIds}
        />
      )}
    </Drawer>
  );
}
